import clsx from "clsx";
import { AnimatePresence, motion } from "framer-motion";
import gsap from "gsap";
import { useEffect, useRef, useState } from "react";
import { TiLocationArrow, TiThMenu } from "react-icons/ti";
import { useLocation, useNavigate } from "react-router-dom";
import { useWindowScroll } from "react-use";

import zeroBlockchainImg from "../assets/images/ZeroBankLogoLong.webp";
import { moreNavLists, navLists } from "../constants";
import Button from "./Button";

const AUDIO_PLAYLIST = [
  "/audio/zero-blockchain-audio (1).mp3",
  "/audio/zero-blockchain-audio (2).mp3",
  "/audio/zero-blockchain-audio (3).mp3",
  "/audio/zero-blockchain-audio (4).mp3",
  "/audio/zero-blockchain-audio (5).mp3",
  "/audio/zero-blockchain-audio (6).mp3",
  "/audio/zero-blockchain-audio (7).mp3",
  "/audio/zero-blockchain-audio (8).mp3",
  "/audio/zero-blockchain-audio (9).mp3",
  "/audio/zero-blockchain-audio (10).mp3",
  "/audio/zero-blockchain-audio (11).mp3",
  "/audio/zero-blockchain-audio (12).mp3",
];

const Navbar = () => {
  const [isAudioPlaying, setIsAudioPlaying] = useState(false);
  const [isIndicatorActive, setIsIndicatorActive] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [currentTrackIndex, setCurrentTrackIndex] = useState(0);

  const audioElementRef = useRef(null);
  const navContainerRef = useRef(null);

  const { y: currentScrollY } = useWindowScroll();
  const [isNavVisible, setIsNavVisible] = useState(true);
  const [lastScrollY, setLastScrollY] = useState(0);

  const navigate = useNavigate();
  const location = useLocation();

  // Toggle Audio
  const toggleAudioIndicator = () => {
    setIsAudioPlaying((prev) => !prev);
    setIsIndicatorActive((prev) => !prev);
  };

  const handleAudioEnded = () => {
    setCurrentTrackIndex((prevIndex) => (prevIndex + 1) % AUDIO_PLAYLIST.length);
  };

  // Play/Pause Audio Effect
  useEffect(() => {
    if (isAudioPlaying) {
      audioElementRef.current.play();
    } else {
      audioElementRef.current.pause();
    }
  }, [isAudioPlaying, currentTrackIndex]);

  // Show/Hide Navbar on Scroll
  useEffect(() => {
    if (currentScrollY === 0) {
      setIsNavVisible(true);
      navContainerRef.current.classList.remove("floating-nav");
    } else if (currentScrollY > lastScrollY) {
      setIsNavVisible(false);
      navContainerRef.current.classList.add("floating-nav");
    } else if (currentScrollY < lastScrollY) {
      setIsNavVisible(true);
      navContainerRef.current.classList.add("floating-nav");
    }

    setLastScrollY(currentScrollY);
  }, [currentScrollY, lastScrollY]);

  // Animate Navbar Visibility
  useEffect(() => {
    gsap.to(navContainerRef.current, {
      y: isNavVisible ? 0 : -100,
      opacity: isNavVisible ? 1 : 0,
      duration: 0.2,
    });
  }, [isNavVisible]);

  // Map Navigation Items
  const navItems = navLists.map((nav) => {
    const path = nav === "Home" ? "/" : `/${nav.toLowerCase().replace(/\s+/g, '-')}`;
    return {
      id: `${nav.toLowerCase().replace(/\s+/g, '-')}-button`,
      title: nav,
      path,
    };
  });

  // Map Dropdown Items
  const dropdownItems = moreNavLists.map((nav) => {
    const path = `/${nav.toLowerCase().replace(/\s+/g, '-')}`;
    return {
      id: `${nav.toLowerCase().replace(/\s+/g, '-')}-button`,
      title: nav,
      path,
    };
  });

  // Handle Navigation
  const handleNavigate = (path) => {
    navigate(path);
    setIsDropdownOpen(false);
    setIsMenuOpen(false);
  };

  return (
    <div
        ref={navContainerRef}
        className="fixed inset-x-0 top-4 z-50 h-16 border-none transition-all duration-700 sm:inset-x-6"
      >
        <header className="absolute top-1/2 w-full -translate-y-1/2">
          {/* Glowing border effect */}
          <div className="absolute inset-0 rounded-2xl bg-gradient-to-r from-cyan-500/20 via-transparent to-cyan-500/20 blur-xl opacity-50" />
          
          <nav className="relative flex items-center justify-between p-4 backdrop-blur-md bg-black/30 rounded-2xl border border-white/10">
            {/* Logo */}
            <div className="flex items-center gap-7">
              <motion.img
                src={zeroBlockchainImg}
                alt="Zero Blockchain Logo"
                className="w-[150px] cursor-pointer"
                onClick={() => window.open("https://www.zero-bank.xyz", "_blank")}
                whileHover={{ scale: 1.05 }}
                transition={{ duration: 0.2 }}
              />

            {/* Mobile Menu Icon */}
            <motion.button
              className="md:hidden text-2xl text-gray-300 hover:text-cyan-400 transition-colors duration-300"
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              aria-label="Toggle Navigation Menu"
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.95 }}
            >
              <TiThMenu />
            </motion.button>
          </div>

          {/* Navigation Buttons */}
          <div className="hidden md:flex space-x-2">
            {navItems.map((nav, index) => (
              <motion.div
                key={nav.id}
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
              >
                <Button
                  id={nav.id}
                  title={nav.title}
                  rightIcon={<TiLocationArrow />}
                  containerClass={clsx(
                    "relative overflow-hidden",
                    {
                      "bg-gradient-to-r from-cyan-500 to-blue-500 text-white border-0 shadow-lg shadow-cyan-500/25": location.pathname === nav.path,
                      "hover:bg-white/10 border border-white/10": location.pathname !== nav.path,
                    }
                  )}
                  onClick={() => handleNavigate(nav.path)}
                />
              </motion.div>
            ))}

            {/* More Button with Enhanced Dropdown */}
            <div className="relative">
              <Button
                id="more-button"
                title="More"
                rightIcon={<TiLocationArrow />}
                containerClass="hover:bg-white/10 rounded-lg border border-white/10"
                onClick={() => setIsDropdownOpen(!isDropdownOpen)}
              />
              <AnimatePresence>
                {isDropdownOpen && (
                  <motion.div
                    initial={{ opacity: 0, y: -10, scale: 0.95 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{ opacity: 0, y: -10, scale: 0.95 }}
                    transition={{ duration: 0.2, ease: "easeOut" }}
                    className="absolute right-0 mt-3 w-[240px] max-h-[80vh] overflow-y-auto z-50"
                  >
                    {/* Animated gradient border container */}
                    <div className="relative rounded-2xl p-[1px] overflow-hidden">
                      {/* Animated gradient border */}
                      <motion.div
                        className="absolute inset-0 rounded-2xl"
                        style={{
                          background: "linear-gradient(90deg, #06b6d4, #3b82f6, #8b5cf6, #ec4899, #06b6d4)",
                          backgroundSize: "200% 100%",
                        }}
                        animate={{
                          backgroundPosition: ["0% 50%", "200% 50%"],
                        }}
                        transition={{
                          duration: 3,
                          repeat: Infinity,
                          ease: "linear",
                        }}
                      />
                      
                      {/* Outer glow */}
                      <div className="absolute -inset-2 rounded-2xl bg-gradient-to-r from-cyan-500/30 via-blue-500/20 to-purple-500/30 blur-xl opacity-50" />
                      
                      {/* Inner content container */}
                      <div className="relative bg-black/95 backdrop-blur-xl rounded-2xl overflow-hidden">
                        {/* Spotlight effect */}
                        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-32 h-32 bg-cyan-500/10 rounded-full blur-3xl" />
                        
                        {/* Category: User Features */}
                        <div className="relative z-10 px-2 pt-2 pb-1">
                          <div className="px-3 py-1 text-[10px] font-semibold uppercase tracking-wider text-cyan-400/70 flex items-center gap-2">
                            <span className="w-3 h-[1px] bg-gradient-to-r from-transparent to-cyan-400/50" />
                            User Features
                            <span className="flex-1 h-[1px] bg-gradient-to-r from-cyan-400/50 to-transparent" />
                          </div>
                          {["Governance", "Dashboard", "Portfolio", "NFT Marketplace"].map((item, index) => (
                            <motion.button
                              key={item}
                              initial={{ opacity: 0, x: -10 }}
                              animate={{ opacity: 1, x: 0 }}
                              transition={{ delay: index * 0.03 }}
                              className="group relative flex items-center w-full px-3 py-1.5 text-left text-sm text-gray-300 rounded-md overflow-hidden transition-all duration-300"
                              onClick={() => handleNavigate(`/${item.toLowerCase().replace(/\s+/g, '-')}`)}
                            >
                              {/* Hover background */}
                              <div className="absolute inset-0 bg-gradient-to-r from-cyan-500/0 via-cyan-500/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                              {/* Left accent */}
                              <div className="absolute left-0 top-1/2 -translate-y-1/2 w-[2px] h-0 bg-gradient-to-b from-cyan-400 to-blue-500 rounded-full group-hover:h-4 transition-all duration-300" />
                              <span className="relative z-10 pl-2 group-hover:text-white group-hover:translate-x-0.5 transition-all duration-300">{item}</span>
                            </motion.button>
                          ))}
                        </div>

                        {/* Divider */}
                        <div className="mx-3 h-[1px] bg-gradient-to-r from-transparent via-white/10 to-transparent" />

                        {/* Category: Company Info */}
                        <div className="relative z-10 px-2 pt-1 pb-1">
                          <div className="px-3 py-1 text-[10px] font-semibold uppercase tracking-wider text-blue-400/70 flex items-center gap-2">
                            <span className="w-3 h-[1px] bg-gradient-to-r from-transparent to-blue-400/50" />
                            Company Info
                            <span className="flex-1 h-[1px] bg-gradient-to-r from-blue-400/50 to-transparent" />
                          </div>
                          {["Partners", "Blog", "Careers", "Contact Us"].map((item, index) => (
                            <motion.button
                              key={item}
                              initial={{ opacity: 0, x: -10 }}
                              animate={{ opacity: 1, x: 0 }}
                              transition={{ delay: 0.12 + index * 0.03 }}
                              className="group relative flex items-center w-full px-3 py-1.5 text-left text-sm text-gray-300 rounded-md overflow-hidden transition-all duration-300"
                              onClick={() => handleNavigate(`/${item.toLowerCase().replace(/\s+/g, '-')}`)}
                            >
                              <div className="absolute inset-0 bg-gradient-to-r from-blue-500/0 via-blue-500/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                              <div className="absolute left-0 top-1/2 -translate-y-1/2 w-[2px] h-0 bg-gradient-to-b from-blue-400 to-purple-500 rounded-full group-hover:h-4 transition-all duration-300" />
                              <span className="relative z-10 pl-2 group-hover:text-white group-hover:translate-x-0.5 transition-all duration-300">{item}</span>
                            </motion.button>
                          ))}
                        </div>

                        {/* Divider */}
                        <div className="mx-3 h-[1px] bg-gradient-to-r from-transparent via-white/10 to-transparent" />

                        {/* Category: Technical */}
                        <div className="relative z-10 px-2 pt-1 pb-1">
                          <div className="px-3 py-1 text-[10px] font-semibold uppercase tracking-wider text-purple-400/70 flex items-center gap-2">
                            <span className="w-3 h-[1px] bg-gradient-to-r from-transparent to-purple-400/50" />
                            Technical
                            <span className="flex-1 h-[1px] bg-gradient-to-r from-purple-400/50 to-transparent" />
                          </div>
                          {["Developers", "Market Data", "Compliance"].map((item, index) => (
                            <motion.button
                              key={item}
                              initial={{ opacity: 0, x: -10 }}
                              animate={{ opacity: 1, x: 0 }}
                              transition={{ delay: 0.24 + index * 0.03 }}
                              className="group relative flex items-center w-full px-3 py-1.5 text-left text-sm text-gray-300 rounded-md overflow-hidden transition-all duration-300"
                              onClick={() => handleNavigate(`/${item.toLowerCase().replace(/\s+/g, '-')}`)}
                            >
                              <div className="absolute inset-0 bg-gradient-to-r from-purple-500/0 via-purple-500/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                              <div className="absolute left-0 top-1/2 -translate-y-1/2 w-[2px] h-0 bg-gradient-to-b from-purple-400 to-pink-500 rounded-full group-hover:h-4 transition-all duration-300" />
                              <span className="relative z-10 pl-2 group-hover:text-white group-hover:translate-x-0.5 transition-all duration-300">{item}</span>
                            </motion.button>
                          ))}
                        </div>

                        {/* Divider */}
                        <div className="mx-3 h-[1px] bg-gradient-to-r from-transparent via-white/10 to-transparent" />

                        {/* Category: Legal */}
                        <div className="relative z-10 px-2 pt-1 pb-2">
                          <div className="px-3 py-1 text-[10px] font-semibold uppercase tracking-wider text-gray-500 flex items-center gap-2">
                            <span className="w-3 h-[1px] bg-gradient-to-r from-transparent to-gray-500/50" />
                            Legal
                            <span className="flex-1 h-[1px] bg-gradient-to-r from-gray-500/50 to-transparent" />
                          </div>
                          {["Privacy Policy", "Terms of Use", "Sales Policy", "Legal", "Site Map"].map((item, index) => (
                            <motion.button
                              key={item}
                              initial={{ opacity: 0, x: -10 }}
                              animate={{ opacity: 1, x: 0 }}
                              transition={{ delay: 0.33 + index * 0.03 }}
                              className="group relative flex items-center w-full px-3 py-1 text-left text-xs text-gray-300 rounded-md overflow-hidden transition-all duration-300"
                              onClick={() => handleNavigate(`/${item.toLowerCase().replace(/\s+/g, '-')}`)}
                            >
                              <div className="absolute inset-0 bg-gradient-to-r from-gray-500/0 via-gray-500/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                              <div className="absolute left-0 top-1/2 -translate-y-1/2 w-[2px] h-0 bg-gray-500 rounded-full group-hover:h-3 transition-all duration-300" />
                              <span className="relative z-10 pl-2 group-hover:text-gray-200 group-hover:translate-x-0.5 transition-all duration-300">{item}</span>
                            </motion.button>
                          ))}
                        </div>
                      </div>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>

          {/* Audio Indicator */}
          <div className="flex items-center gap-7">
            <motion.button
              onClick={toggleAudioIndicator}
              className="flex items-center space-x-0.5 p-2 rounded-full hover:bg-white/10 transition-colors"
              aria-label="Toggle Audio"
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.95 }}
            >
              <audio
                ref={audioElementRef}
                className="hidden"
                src={AUDIO_PLAYLIST[currentTrackIndex]}
                onEnded={handleAudioEnded}
              />
              {[1, 2, 3, 4].map((bar) => (
                <div
                  key={bar}
                  className={clsx("indicator-line", {
                    active: isIndicatorActive,
                  })}
                  style={{
                    animationDelay: `${bar * 0.1}s`,
                    background: isIndicatorActive ? 'linear-gradient(to top, #06b6d4, #3b82f6)' : undefined,
                  }}
                />
              ))}
            </motion.button>
          </div>
        </nav>

        {/* Mobile Menu */}
        <AnimatePresence>
          {isMenuOpen && (
            <motion.div
              initial={{ opacity: 0, y: -20, scale: 0.95 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: -20, scale: 0.95 }}
              transition={{ duration: 0.3 }}
              className="absolute top-20 right-0 left-0 mx-4 bg-black/95 backdrop-blur-xl rounded-2xl shadow-2xl shadow-cyan-500/20 p-5 flex flex-col space-y-3 border border-white/10"
            >
              {navItems.map((nav, index) => (
                <motion.div
                  key={nav.id}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.1 }}
                >
                  <Button
                    id={nav.id}
                    title={nav.title}
                    rightIcon={<TiLocationArrow />}
                    containerClass={clsx(
                      "w-full justify-center",
                      location.pathname === nav.path
                        ? "bg-gradient-to-r from-cyan-500 to-blue-500 text-white"
                        : "border border-white/10 hover:bg-white/10"
                    )}
                    onClick={() => handleNavigate(nav.path)}
                  />
                </motion.div>
              ))}

              {/* Mobile Dropdown for More */}
              <div>
                <Button
                  id="mobile-more-button"
                  title="More"
                  containerClass="w-full justify-center border border-white/10 hover:bg-white/10"
                  onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                />
                <AnimatePresence>
                  {isDropdownOpen && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: 'auto' }}
                      exit={{ opacity: 0, height: 0 }}
                      className="mt-3 rounded-xl overflow-hidden border border-white/10 bg-black/80 backdrop-blur-xl"
                    >
                      {/* Mobile Category: User Features */}
                      <div className="p-2">
                        <div className="px-3 py-1.5 text-xs font-semibold uppercase tracking-wider text-cyan-400/70">User Features</div>
                        {["Governance", "Dashboard", "Portfolio", "NFT Marketplace"].map((item, index) => (
                          <motion.button
                            key={item}
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            transition={{ delay: index * 0.03 }}
                            className="group flex items-center w-full px-3 py-2 text-left text-gray-300 rounded-lg hover:bg-cyan-500/10 transition-colors"
                            onClick={() => handleNavigate(`/${item.toLowerCase().replace(/\s+/g, '-')}`)}
                          >
                            <div className="w-1 h-4 bg-gradient-to-b from-cyan-400 to-blue-500 rounded-full mr-3 opacity-50 group-hover:opacity-100 transition-opacity" />
                            {item}
                          </motion.button>
                        ))}
                      </div>
                      
                      <div className="mx-3 h-[1px] bg-gradient-to-r from-transparent via-white/10 to-transparent" />
                      
                      {/* Mobile Category: Company Info */}
                      <div className="p-2">
                        <div className="px-3 py-1.5 text-xs font-semibold uppercase tracking-wider text-blue-400/70">Company Info</div>
                        {["Partners", "Blog", "Careers", "Contact Us"].map((item, index) => (
                          <motion.button
                            key={item}
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            transition={{ delay: 0.12 + index * 0.03 }}
                            className="group flex items-center w-full px-3 py-2 text-left text-gray-300 rounded-lg hover:bg-blue-500/10 transition-colors"
                            onClick={() => handleNavigate(`/${item.toLowerCase().replace(/\s+/g, '-')}`)}
                          >
                            <div className="w-1 h-4 bg-gradient-to-b from-blue-400 to-purple-500 rounded-full mr-3 opacity-50 group-hover:opacity-100 transition-opacity" />
                            {item}
                          </motion.button>
                        ))}
                      </div>
                      
                      <div className="mx-3 h-[1px] bg-gradient-to-r from-transparent via-white/10 to-transparent" />
                      
                      {/* Mobile Category: Technical */}
                      <div className="p-2">
                        <div className="px-3 py-1.5 text-xs font-semibold uppercase tracking-wider text-purple-400/70">Technical</div>
                        {["Developers", "Market Data", "Compliance"].map((item, index) => (
                          <motion.button
                            key={item}
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            transition={{ delay: 0.24 + index * 0.03 }}
                            className="group flex items-center w-full px-3 py-2 text-left text-gray-300 rounded-lg hover:bg-purple-500/10 transition-colors"
                            onClick={() => handleNavigate(`/${item.toLowerCase().replace(/\s+/g, '-')}`)}
                          >
                            <div className="w-1 h-4 bg-gradient-to-b from-purple-400 to-pink-500 rounded-full mr-3 opacity-50 group-hover:opacity-100 transition-opacity" />
                            {item}
                          </motion.button>
                        ))}
                      </div>
                      
                      <div className="mx-3 h-[1px] bg-gradient-to-r from-transparent via-white/10 to-transparent" />
                      
                      {/* Mobile Category: Legal */}
                      <div className="p-2">
                        <div className="px-3 py-1.5 text-xs font-semibold uppercase tracking-wider text-gray-500">Legal</div>
                        {["Privacy Policy", "Terms of Use", "Sales Policy", "Legal", "Site Map"].map((item, index) => (
                          <motion.button
                            key={item}
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            transition={{ delay: 0.33 + index * 0.03 }}
                            className="group flex items-center w-full px-3 py-2 text-left text-gray-300 text-sm rounded-lg hover:bg-gray-500/10 transition-colors"
                            onClick={() => handleNavigate(`/${item.toLowerCase().replace(/\s+/g, '-')}`)}
                          >
                            <div className="w-1 h-3 bg-gray-500 rounded-full mr-3 opacity-50 group-hover:opacity-100 transition-opacity" />
                            {item}
                          </motion.button>
                        ))}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </header>
    </div>
  );
};

export default Navbar;
